import React, { useEffect, useState, useRef } from "react";
import CategoryIcon from "@mui/icons-material/Category";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import TableCard from "../../components/TableCard";
import {
  atualizarCategoriasVerbas,
  buscarCategoriasVerbas,
  buscarCategoriaVerbas,
  buscarCategoriasVerbasPorTexto,
  deletarCategoriasVerbas,
  inserirCategoriasVerbas,
} from "../../services/api-planejamento-tributario.service";
import SnackBar from "../../components/SnackBar";
import {
  Autocomplete,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  TextField,
  Button,
} from "@mui/material";
import Loading from "../../components/Loading";

const CategoriasVerbas = () => {
  const [openEditModal, setOpenEditModal] = useState(false);
  const [data, setData] = useState([]); // dados organizados para tabela
  const [rawData, setRawData] = useState([]); // dados crus vindos da API (para re-map quando combo carregar)
  const [loading, setLoading] = useState(false);
  const [objSnackBar, setObjSnackBar] = useState({ type: "", msg: "" });
  const [openDeleteModal, setOpenDeleteModal] = useState(false);

  // formData: fields used in modal
  const [formData, setFormData] = useState({
    idCategoriaTributaria: null, // compatibilidade
    idVerbaCliente: null, // id da verba quando aplicável
    codigo: "",
    descricao: "",
    categoriaVerba: null, // objeto selecionado { id, descricao }
  });

  const [comboCategoriaVerbas, setComboCategoriaVerbas] = useState([]); // { id, descricao }
  const [dados, setDados] = useState();
  const [isUpdate, setIsUpdate] = useState(false);
  const searchTimer = useRef(null);

  // carrega verbas -> salva rawData e mapeia para a tabela
  const handleDadosCategorias = async () => {
    try {
      setLoading(true);
      const response = await buscarCategoriasVerbas();
      const arr = response?.categories || response?.categorias || response || [];
      setRawData(arr);
      setData(await handleOrganizaData(arr, comboCategoriaVerbas));
    } catch (error) {
      setObjSnackBar({ type: "error", msg: error?.response?.data?.message || "Erro ao carregar categorias" });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      await handleDadosCategorias();

      try {
        // carregar opções do combo (categorias de verbas)
        const resp = await buscarCategoriaVerbas();
        const itens = resp?.categorias || resp || [];

        // map para { id, descricao } — label será descricao, value será id
        const categoriaVerbas = itens.map((item) => {
          return {
            id: Number(item.idCategoriaVerba ?? item.id ?? item.idCategoriaVerba),
            descricao: item.descricao ?? item.detalhes ?? String(item.idCategoriaVerba ?? item.id),
          };
        });

        setComboCategoriaVerbas(categoriaVerbas);

        // após carregar o combo, reaplicar mapeamento (para popular a coluna 'categoria' com nome correto)
        if (rawData && rawData.length > 0) {
          setData(await handleOrganizaData(rawData, categoriaVerbas));
        }
      } catch (err) {
        console.warn("Não foi possível carregar combo de categorias de verbas:", err);
        setComboCategoriaVerbas([]);
      }

      setLoading(false);
    };

    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Se o combo mudar em runtime (ex.: recarregado), remapeia a tabela usando rawData
  useEffect(() => {
    (async () => {
      if (rawData && rawData.length > 0) {
        setData(await handleOrganizaData(rawData, comboCategoriaVerbas));
      }
    })();
  }, [comboCategoriaVerbas, rawData]);

  const handleSnackBar = (obj) => setObjSnackBar(obj);

  // abre modal para edição/criação — se "item" undefined => criar novo
  const handleOpenEditModal = (item) => {
    if (item) {
      setIsUpdate(true);
      setDados(item);
    } else {
      setIsUpdate(false);
      setDados({
        idCategoriaTributaria: null,
        idVerbaCliente: null,
        codigo: "",
        descricao: "",
        idCategoria: null,
      });
    }
  };

  // quando dados para edição são definidos via handleOpenEditModal, abre o modal populando formData
  useEffect(() => {
    if (dados !== undefined) {
      const idVerbaCliente = dados.idVerbaCliente ?? dados.idCategoriaTributaria ?? null;
      const codigo = dados.codigo ?? dados.cod ?? "";
      const descricao = dados.descricao ?? "";

      // id da categoria vindo no item (pode ser idCategoria ou idCategoriaVerba)
      const categoriaFromResponseId = dados.idCategoria ?? dados.idCategoriaVerba ?? null;
      const categoriaObj =
        comboCategoriaVerbas.find((c) => Number(c.id) === Number(categoriaFromResponseId)) || null;

      setFormData({
        idCategoriaTributaria: dados.idCategoriaTributaria ?? null,
        idVerbaCliente,
        codigo,
        descricao,
        categoriaVerba: categoriaObj,
      });

      setOpenEditModal(true);
    }
  }, [dados, comboCategoriaVerbas]);

  const handleCloseEditModal = () => {
    setOpenEditModal(false);
    setIsUpdate(false);
    setDados(undefined);
    setFormData({
      idCategoriaTributaria: null,
      idVerbaCliente: null,
      codigo: "",
      descricao: "",
      categoriaVerba: null,
    });
  };

  const handleDescricaoChange = (e) => {
    setFormData({ ...formData, descricao: e.target.value });
  };

  const handleCodigoChange = (e) => {
    setFormData({ ...formData, codigo: e.target.value });
  };

  const handleCategoriaVerbaSelect = (_, value) => {
    // value is object { id, descricao } or null
    // **IMPORTANTE**: por requisito, ao selecionar outra categoria **APENAS** alteramos idCategoria
    // não alteramos a descrição do registro here.
    setFormData(prev => ({
      ...prev,
      categoriaVerba: value || null,
    }));
  };

  // salvar (create / update)
  const handleSave = async (e) => {
    e.preventDefault();
    try {
      if (!formData.descricao || !formData.codigo) {
        return setObjSnackBar({ type: "error", msg: "Código e descrição são obrigatórios." });
      }

      // payload: cod/codigo, descricao, idCategoria
      const payload = {
        codigo: formData.codigo,
        descricao: formData.descricao,
        idCategoria: formData.categoriaVerba ? Number(formData.categoriaVerba.id) : null,
      };

      let response;
      if (isUpdate) {
        const idToUpdate = formData.idVerbaCliente ?? formData.idCategoriaTributaria;
        if (!idToUpdate) {
          setObjSnackBar({ type: "error", msg: "ID para atualização não encontrado." });
          return;
        }
        response = await atualizarCategoriasVerbas(idToUpdate, payload);
      } else {
        response = await inserirCategoriasVerbas(payload);
      }

      setObjSnackBar({ type: "success", msg: response?.message || response?.mensagem || "Operação realizada com sucesso." });
      await handleDadosCategorias();
      handleCloseEditModal();
    } catch (error) {
      console.error("Erro ao salvar categoria/verba:", error);
      setObjSnackBar({ type: "error", msg: error?.response?.data?.mensagem || error?.response?.data?.message || "Erro ao salvar" });
    }
  };

  const handleDelete = async (e) => {
    e.preventDefault();
    try {
      const idToDelete = formData.idVerbaCliente ?? formData.idCategoriaTributaria;
      if (!idToDelete) {
        setObjSnackBar({ type: "error", msg: "ID para exclusão não informado." });
        return;
      }
      const response = await deletarCategoriasVerbas(idToDelete);
      setObjSnackBar({ type: "success", msg: response?.message || "Removido com sucesso." });
      await handleDadosCategorias();
    } catch (error) {
      console.error("Erro ao excluir:", error);
      setObjSnackBar({ type: "error", msg: error?.response?.data?.mensagem || "Erro ao excluir" });
    }
    setOpenDeleteModal(false);
  };

  const handleDebounce = (value) => {
    if (searchTimer.current) clearTimeout(searchTimer.current);
    searchTimer.current = setTimeout(() => {
      handlePesquisaPorTexto(value);
    }, 500);
  };

  const handlePesquisaPorTexto = async (value) => {
    try {
      setLoading(true);

      // ensure we have the full dataset in rawData; if empty, fetch once
      let source = rawData && rawData.length > 0 ? rawData : (await buscarCategoriasVerbas()).categories || (await buscarCategoriasVerbas()).categorias || await buscarCategoriasVerbas() || [];

      const term = String(value || "").trim();
      const cleanedInput = term.replace(/\D/g, "");

      let filtered = source;

      if (term === "") {
        // empty search -> show full dataset
        filtered = source;
      } else if (/^\d+$/.test(term)) {
        // all digits -> CNPJ search using only numeric comparison
        filtered = source.filter((item) => {
          const rawSchema = item.schema || item.schema_name || item.schemaName || "";
          const digits = String(rawSchema).replace(/\D/g, "");
          const cnpjFromSchema = digits.length >= 14 ? digits.slice(-14) : "";
          const cnpjFromField = item.cnpj ? String(item.cnpj).replace(/\D/g, "") : "";
          const cnpjCandidate = cnpjFromSchema || cnpjFromField || "";
          return cnpjCandidate.includes(cleanedInput);
        });
      } else {
        // text search: match codigo or descricao (client-side)
        const lower = term.toLowerCase();
        filtered = source.filter((item) => {
          const codigo = String(item.codigo ?? item.cod ?? "").toLowerCase();
          const descricao = String(item.descricao ?? item.descricaoCategoria ?? "").toLowerCase();
          return codigo.includes(lower) || descricao.includes(lower);
        });
      }

      setData(await handleOrganizaData(filtered, comboCategoriaVerbas));
    } catch (error) {
      console.error("Erro na busca local:", error);
      setObjSnackBar({ type: "error", msg: error?.response?.data?.message || "Erro na busca" });
    } finally {
      setLoading(false);
    }
  };

  const handleOrganizaData = async (arr, comboCategoriaVerbas) => {
    // mapeia dados para formato da tabela (ordem das chaves corresponde às colunas exibidas)
    const dadosTabela = arr.map((item) => {
      const idCategoria = item.idCategoria ?? item.idCategoriaVerba ?? null;
      const codigo = item.codigo ?? item.cod ?? "";
      const descricao = item.descricao ?? item.descricaoCategoria ?? "";
      const rawSchema = item.schema || item.schema_name || item.schemaName || "";
      const digits = String(rawSchema).replace(/\D/g, "");
      const cnpjRaw = digits.length >= 14 ? digits.slice(-14) : (item.cnpj ? String(item.cnpj).replace(/\D/g, "") : "");
      const formatCnpj = (cnpj) => {
        if (!cnpj) return "";
        const s = String(cnpj).padStart(14, "0");
        return s.replace(/(\d{2})(\d{3})(\d{3})(\d{4})(\d{2})/, "$1.$2.$3/$4-$5");
      };
      const cnpjFormatted = formatCnpj(cnpjRaw);

      const categoriaObj = comboCategoriaVerbas.find((c) => Number(c.id) === Number(idCategoria)) || null;

      return {
        // ordem: codigo, cnpj, descricao, categoria (deve corresponder ao array `columns` enviado ao TableCard)
        codigo,
        cnpj: cnpjFormatted,
        descricao,
        categoria: categoriaObj ? categoriaObj.descricao : "",
        // botoes de ação que referenciam o objeto original para edição/exclusão
        botoes: [
          {
            icon: <EditIcon />,
            title: "EDITAR",
            onClick: () => {
              setIsUpdate(true);
              handleOpenEditModal({
                idCategoriaTributaria: item.idCategoria ?? item.idCategoriaVerba ?? null,
                idVerbaCliente: item.idVerbaCliente ?? null,
                codigo,
                descricao,
                idCategoria: idCategoria ?? null,
              });
            },
          },
        ],
      };
    });

    return dadosTabela;
  };

  return (
    <>
      <SnackBar objSnackBar={objSnackBar} setObjSnackBar={setObjSnackBar} />
      <Loading loading={loading} />
      <Dialog open={openEditModal} onClose={handleCloseEditModal} fullWidth maxWidth="sm">
        <DialogTitle>{isUpdate ? "Editar Categoria/Verba" : "Nova Categoria/Verba"}</DialogTitle>
        <DialogContent>
          <Autocomplete
            options={comboCategoriaVerbas}
            getOptionLabel={(option) => option.descricao || ""}
            value={formData.categoriaVerba}
            onChange={handleCategoriaVerbaSelect}
            renderInput={(params) => <TextField {...params} label="Categoria de Verba" variant="outlined" fullWidth sx={{ mt: '5px' }} />}
            isOptionEqualToValue={(option, value) => option.id === value.id}
          />
          <TextField
            label="Código"
            variant="outlined"
            fullWidth
            margin="normal"
            value={formData.codigo}
            onChange={handleCodigoChange}
          />
          <TextField
            label="Descrição"
            variant="outlined"
            fullWidth
            margin="normal"
            value={formData.descricao}
            onChange={handleDescricaoChange}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseEditModal} variant="outlined" color="inherit">Cancelar</Button>
          <Button onClick={handleSave} variant="contained" color="primary">{isUpdate ? "Atualizar" : "Salvar"}</Button>
        </DialogActions>
      </Dialog>

      <Dialog open={openDeleteModal} onClose={() => setOpenDeleteModal(false)}>
        <DialogTitle>Confirmação de Exclusão</DialogTitle>
        <DialogContent>
          Tem certeza que deseja excluir esta categoria/verba?
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenDeleteModal(false)} variant="outlined" color="inherit">Cancelar</Button>
          <Button onClick={handleDelete} variant="contained" color="error">Excluir</Button>
        </DialogActions>
      </Dialog>

      <TableCard
         iconHeader={<CategoryIcon fontSize="1rem" />}
         titleHeader="Embazamento Legal"
          columns={["Código", "CNPJ", "Descrição", "Categoria"]}
           data={data}
           loading={loading}
           onEdit={handleOpenEditModal}
           onDelete={(item) => {
             setDados(item);
             setOpenDeleteModal(true);
           }}
           handleDebounce={handleDebounce}
           searchPlaceholder="Buscar por código, descrição ou CNPJ"
           actions={{
             add: () => handleOpenEditModal(undefined),
             // exportar: true, // se precisar da opção de exportação
           }}
         />
    </>
  );
};

export default CategoriasVerbas;
